/**
 * `/templates/:id` when the template is a WhatsApp one.
 *
 * The same route as the email editor, because a template is a template from the
 * list's point of view, and a separate URL would mean the list had to know which
 * screen a row leads to before it could draw a link. `TemplateEditorPage` reads
 * the template and hands it here when `type === 'whatsapp'`.
 *
 * The screen is three parts: the composer, what Meta says about it, and the
 * preview of what a phone actually shows. The preview is deliberately last on
 * narrow screens and beside the composer on wide ones — it is a check, not the
 * thing being edited.
 */
import type { Template } from '@wend/shared';
import { Badge, Card, CardContent, CardHeader, CardTitle, buttonVariants } from '@wend/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { AppLayout } from '../../../components/AppLayout';
import { TEMPLATE_STATUS_TONE } from '../../../components/TemplateStatusBadge';
import { formatDateTime } from '../../../lib/format';
import { WA_STATUS_DISPLAY } from '../../../lib/whatsapp-display';
import { integrationTabPath } from '../../integrations/integration-tab-url';
import { WaApprovalPanel } from './WaApprovalPanel';
import { waDraftOf, waDraftSignature, type WaDraft } from './wa-draft';
import { WaComposer } from './WaComposer';
import { WaPreview } from './WaPreview';

export function WaTemplateEditorPage({
  loaded,
  onReload,
}: {
  loaded: Template;
  onReload: () => void;
}) {
  const [template, setTemplate] = useState<Template>(loaded);
  const [draft, setDraft] = useState<WaDraft>(() => waDraftOf(loaded));
  const [savedSignature, setSavedSignature] = useState(() => waDraftSignature(waDraftOf(loaded)));

  const whatsapp = template.whatsapp;
  if (whatsapp === null) {
    // Unreachable through the branch that renders this, and cheap insurance
    // against an email template arriving here with no WhatsApp document.
    throw new Error('This is not a WhatsApp template.');
  }

  const dirty = waDraftSignature(draft) !== savedSignature;
  // Meta owns a template it is reviewing or has approved: the document it
  // reviewed and the document Wend stores must not diverge. A rejection is the
  // one state that reopens editing.
  const readOnly = template.status === 'pending' || template.status === 'approved';
  const status = WA_STATUS_DISPLAY[template.status];

  return (
    <AppLayout
      title={template.name}
      actions={
        <Link to="/templates" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All templates
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone="neutral">WhatsApp</Badge>
          <Badge tone={TEMPLATE_STATUS_TONE[template.status]}>{status.label}</Badge>
          {dirty && <Badge tone="warn">Unsaved changes</Badge>}
          <span className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
            updated {formatDateTime(template.updatedAt)}
          </span>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="flex flex-col gap-5">
            <WaComposer
              template={template}
              draft={draft}
              readOnly={readOnly}
              onDraftChange={setDraft}
              onSaved={(saved) => {
                setTemplate(saved);
                const next = waDraftOf(saved);
                setDraft(next);
                setSavedSignature(waDraftSignature(next));
              }}
            />

            <WaApprovalPanel
              template={template}
              whatsapp={whatsapp}
              draft={draft}
              dirty={dirty}
              onChanged={onReload}
            />
          </div>

          <div className="xl:sticky xl:top-6 xl:self-start">
            <Card>
              <CardHeader>
                <CardTitle className="text-[1rem]">On a phone</CardTitle>
              </CardHeader>
              <CardContent>
                <WaPreview draft={draft} />
              </CardContent>
            </Card>

            <div className="mt-4">
              <Link
                to={integrationTabPath('whatsapp')}
                className={buttonVariants({ variant: 'ghost', size: 'sm' })}
              >
                Check the WhatsApp connection
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
